import React from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { sortRequestOptionsByConfiguredOrder } from './optionSorting';

export type OptionType = 'role' | 'organization' | 'site';

type UserAccountRequestOption = {
  id: number;
  optionType: OptionType;
  label: string;
  sortOrder: number;
  isActive: boolean;
  defaultPrograms?: string[] | null;
};

type OptionsQueryResponse = {
  userAccountRequestOptions: UserAccountRequestOption[];
};

type OptionMutationResponse = {
  createUserAccountRequestOption?: {
    success?: boolean;
    message?: string;
    optionId?: number;
  };
  updateUserAccountRequestOption?: {
    success?: boolean;
    message?: string;
    optionId?: number;
  };
  deleteUserAccountRequestOption?: {
    success?: boolean;
    message?: string;
    optionId?: number;
  };
};

const LIST_USER_ACCOUNT_REQUEST_OPTIONS = gql`
  query UserAccountRequestOptionsByType($optionType: String!, $includeInactive: Boolean) {
	    userAccountRequestOptions(optionType: $optionType, includeInactive: $includeInactive) {
	      id
	      optionType
	      label
	      sortOrder
	      isActive
	      defaultPrograms
	    }
	  }
	`;

const CREATE_USER_ACCOUNT_REQUEST_OPTION = gql`
  mutation CreateUserAccountRequestOption($input: UserAccountRequestOptionInput!) {
    createUserAccountRequestOption(input: $input) {
      success
      message
      optionId
    }
  }
`;

const UPDATE_USER_ACCOUNT_REQUEST_OPTION = gql`
  mutation UpdateUserAccountRequestOption($id: Int!, $input: UserAccountRequestOptionInput!) {
    updateUserAccountRequestOption(id: $id, input: $input) {
      success
      message
      optionId
    }
  }
`;

const DELETE_USER_ACCOUNT_REQUEST_OPTION = gql`
  mutation DeleteUserAccountRequestOption($id: Int!) {
    deleteUserAccountRequestOption(id: $id) {
      success
      message
      optionId
    }
  }
`;

type UserAccountRequestOptionEditorProps = {
  optionType: OptionType;
};

const DEFAULT_PROGRAM_OPTIONS = ['FIST', 'MIST', 'ODRH', 'Maternal Health', 'LEAD', 'DSH'];

const normalizeProgramList = (values?: string[] | null) => {
  const seen = new Set<string>();
  const programs: string[] = [];

  (Array.isArray(values) ? values : []).forEach((value) => {
    const normalized = `${value || ''}`.trim();
    const program = DEFAULT_PROGRAM_OPTIONS.find((entry) => entry.toLowerCase() === normalized.toLowerCase());
    if (!program || seen.has(program)) return;
    seen.add(program);
    programs.push(program);
  });

  return programs;
};

export const UserAccountRequestOptionEditor: React.FC<UserAccountRequestOptionEditorProps> = ({ optionType }) => {
  const [editingOptionId, setEditingOptionId] = React.useState<number | null>(null);
  const [label, setLabel] = React.useState('');
  const [sortOrder, setSortOrder] = React.useState('0');
  const [isActive, setIsActive] = React.useState(true);
  const [defaultPrograms, setDefaultPrograms] = React.useState<string[]>([]);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const { data, loading, error, refetch } = useQuery<OptionsQueryResponse>(LIST_USER_ACCOUNT_REQUEST_OPTIONS, {
    variables: {
      optionType,
      includeInactive: true
    },
    fetchPolicy: 'cache-and-network'
  });

  const [createOption] = useMutation<OptionMutationResponse>(CREATE_USER_ACCOUNT_REQUEST_OPTION);
  const [updateOption] = useMutation<OptionMutationResponse>(UPDATE_USER_ACCOUNT_REQUEST_OPTION);
  const [deleteOption] = useMutation<OptionMutationResponse>(DELETE_USER_ACCOUNT_REQUEST_OPTION);

  const options = React.useMemo(() => {
    const rows = Array.isArray(data?.userAccountRequestOptions) ? data?.userAccountRequestOptions || [] : [];
    return sortRequestOptionsByConfiguredOrder(rows);
  }, [data?.userAccountRequestOptions]);

	  const resetEditor = React.useCallback(() => {
	    setEditingOptionId(null);
	    setLabel('');
	    setSortOrder('0');
	    setIsActive(true);
	    setDefaultPrograms([]);
	  }, []);

  React.useEffect(() => {
    resetEditor();
  }, [optionType, resetEditor]);

	  const handleEdit = (option: UserAccountRequestOption) => {
	    setEditingOptionId(option.id);
	    setLabel(option.label || '');
	    setSortOrder(String(option.sortOrder || 0));
	    setIsActive(Boolean(option.isActive));
	    setDefaultPrograms(normalizeProgramList(option.defaultPrograms));
	  };

  const setProgramSelected = (program: string, selected: boolean) => {
    setDefaultPrograms((prev) => {
      const normalized = normalizeProgramList(prev);
      if (selected) {
        return normalizeProgramList([...normalized, program]);
      }
      return normalized.filter((entry) => entry !== program);
    });
  };

  const handleSave = async () => {
    const cleanLabel = `${label || ''}`.trim();
    if (!cleanLabel) {
      toast.error('Label is required');
      return;
    }

    const parsedSortOrder = Number(sortOrder);
    const normalizedSortOrder = Number.isFinite(parsedSortOrder) ? Math.trunc(parsedSortOrder) : 0;

    setIsSaving(true);
    try {
	      const payload = {
	        optionType,
	        label: cleanLabel,
	        sortOrder: normalizedSortOrder,
	        isActive,
	        defaultPrograms: optionType === 'organization' ? defaultPrograms : []
	      };

      if (editingOptionId) {
        const response = await updateOption({
          variables: {
            id: editingOptionId,
            input: payload
          }
        });

        const result = response?.data?.updateUserAccountRequestOption;
        if (!result?.success) {
          toast.error(result?.message || 'Unable to update option');
          return;
        }

        toast.success('Option updated');
      }

      if (!editingOptionId) {
        const response = await createOption({
          variables: {
            input: payload
          }
        });

        const result = response?.data?.createUserAccountRequestOption;
        if (!result?.success) {
          toast.error(result?.message || 'Unable to create option');
          return;
        }

        toast.success('Option created');
      }

      resetEditor();
      await refetch();
    } catch (saveError: any) {
      toast.error(saveError?.message || 'Failed to save option');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingOptionId) return;

    setIsDeleting(true);
    try {
      const response = await deleteOption({
        variables: {
          id: editingOptionId
        }
      });

      const result = response?.data?.deleteUserAccountRequestOption;
      if (!result?.success) {
        toast.error(result?.message || 'Unable to delete option');
        return;
      }

      toast.success('Option deleted');
      resetEditor();
      await refetch();
    } catch (deleteError: any) {
      toast.error(deleteError?.message || 'Failed to delete option');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="border rounded-md overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50">
          <span className="text-sm font-medium text-gray-700">Configured Values</span>
          <Button
            size="sm"
            variant="outline"
            onClick={resetEditor}
          >
            <Plus className="h-4 w-4 mr-1" />
            New
          </Button>
        </div>

        <div className="max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
	            <thead className="bg-gray-50 sticky top-0">
	              <tr>
	                <th className="text-left p-2">Label</th>
	                {optionType === 'organization' && (
	                  <th className="text-left p-2">Default Access</th>
	                )}
	                <th className="text-left p-2">Sort</th>
	                <th className="text-left p-2">Status</th>
	                <th className="text-left p-2">Action</th>
	              </tr>
	            </thead>
	            <tbody>
	              {loading && (
	                <tr>
	                  <td className="p-2 text-gray-500" colSpan={optionType === 'organization' ? 5 : 4}>
	                    Loading...
	                  </td>
	                </tr>
	              )}
	              {!loading && options.length === 0 && (
	                <tr>
	                  <td className="p-2 text-gray-500" colSpan={optionType === 'organization' ? 5 : 4}>
	                    No values configured.
	                  </td>
	                </tr>
	              )}
	              {!loading && options.map((option) => (
	                <tr key={option.id} className="border-t">
	                  <td className="p-2">{option.label}</td>
	                  {optionType === 'organization' && (
	                    <td className="p-2">{normalizeProgramList(option.defaultPrograms).join(', ') || '-'}</td>
	                  )}
	                  <td className="p-2">{option.sortOrder}</td>
	                  <td className="p-2">{option.isActive ? 'Active' : 'Inactive'}</td>
	                  <td className="p-2">
                    <Button
                      type="button"
                      aria-label={`Edit ${option.label}`}
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(option)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="border rounded-md p-4 space-y-4">
        <h4 className="text-sm font-medium text-gray-900">
          {editingOptionId ? 'Edit Value' : 'New Value'}
        </h4>

        <div className="space-y-1">
          <Label htmlFor="option-label">Label</Label>
          <Input
            id="option-label"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="Enter display label"
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="option-sort">Sort Order</Label>
          <Input
            id="option-sort"
            type="number"
            value={sortOrder}
            onChange={(event) => setSortOrder(event.target.value)}
          />
        </div>

	        <div className="flex items-center justify-between">
	          <Label htmlFor="option-active">Active</Label>
	          <Switch
	            id="option-active"
	            checked={isActive}
	            onCheckedChange={(checked) => setIsActive(Boolean(checked))}
	          />
	        </div>

	        {optionType === 'organization' && (
	          <div className="space-y-2">
	            <Label>Default Program Access</Label>
	            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
	              {DEFAULT_PROGRAM_OPTIONS.map((program) => {
	                const checked = defaultPrograms.includes(program);
	                return (
	                  <label
	                    key={program}
	                    className="flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700"
	                  >
	                    <input
	                      type="checkbox"
	                      checked={checked}
	                      onChange={(event) => setProgramSelected(program, event.target.checked)}
	                    />
	                    <span>{program}</span>
	                  </label>
	                );
	              })}
	            </div>
	          </div>
	        )}

	        <div className="flex items-center gap-2 pt-2">
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : (editingOptionId ? 'Save Changes' : 'Create Value')}
          </Button>

          {editingOptionId && (
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          )}
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
};

export default UserAccountRequestOptionEditor;
